import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { HiringStatusBadge, Percentage } from '~/modules/common/components';
import { RequestStatusLabel } from '~/modules/resourcing/common/components';
import {
  useValueWithPrecision,
  useFetchRequisitionData,
  useIsPtiEnabled
} from '~/modules/common/hooks';
import { ResourcePlanGroupBy } from '~/types';
import { showHiringStatusBadge } from '~/modules/common/components/HiringStatusBadge';
import { ResourceRequestDetailIndicator } from '../components';
import ResourceRequestRoleCount from '../components/ResourceRequestRoleCount';
import useStyles, { useRoleWithQuantityLabelStyles } from './useStyles';

export const ResourceRequestReadonlyItem = ({
  qeIdTag,
  resourceRequest,
  maxSkillLevel,
  displayEntityType = 'ROLE'
}) => {
  const classes = useStyles();
  const roleWithQuantityClasses = useRoleWithQuantityLabelStyles();
  const { formatMessage } = useIntl();

  const isPtiEnabled = useIsPtiEnabled();

  const { requisition } = useFetchRequisitionData({
    resourceRequestUri: resourceRequest.id,
    skip: !isPtiEnabled
  });
  const requisitionHiringStatus = requisition?.status;

  const { project } = resourceRequest;

  const loadValue = useValueWithPrecision({
    value: resourceRequest.load
  });

  const loadingToolTip = useMemo(
    () => (
      <>
        {formatMessage({
          id: 'resourceRequestReadonlyItem.loading'
        })}
        : {loadValue}%
        {resourceRequest.isAdjustedLoading && (
          <div className={classes.tooltipDetails}>
            {formatMessage({
              id: 'resourceRequestReadonlyItem.averageLoading'
            })}
          </div>
        )}
      </>
    ),
    [
      formatMessage,
      loadValue,
      resourceRequest.isAdjustedLoading,
      classes.tooltipDetails
    ]
  );

  return (
    <div className={classes.root}>
      <div className={classes.labelRow}>
        {displayEntityType !== 'ROLE' ? (
          <span className={classes.roleName}>{project.name}</span>
        ) : (
          <ResourceRequestRoleCount
            role={resourceRequest.role}
            roleCount={resourceRequest.quantity}
            classes={roleWithQuantityClasses}
          />
        )}
        <RequestStatusLabel value={resourceRequest.requestStatus} short />
      </div>
      <div
        data-qe-id={qeIdTag ? `${qeIdTag}_RoleDetails` : 'RoleDetails'}
        className={classes.roleDetails}
      >
        <Percentage
          dataQeId={qeIdTag ? `${qeIdTag}_RolePercentage` : 'RolePercentage'}
          title={loadingToolTip}
          value={resourceRequest.load}
          fixedDecimalScale={false}
          className={classNames(classes.loading, {
            [classes.adjustedLoading]: resourceRequest.isAdjustedLoading
          })}
        />
        {resourceRequest.isAdjustedLoading && '*'}
        <ResourceRequestDetailIndicator
          resourceRequest={resourceRequest}
          maxSkillLevel={maxSkillLevel}
        />
      </div>
      {isPtiEnabled && showHiringStatusBadge({ resourceRequest }) && (
        <div className={classes.badgeContainer}>
          <HiringStatusBadge
            requisitionHiringStatus={requisitionHiringStatus}
          />
        </div>
      )}
    </div>
  );
};

ResourceRequestReadonlyItem.propTypes = {
  qeIdTag: PropTypes.string,
  displayEntityType: PropTypes.oneOf([
    ResourcePlanGroupBy.Project,
    ResourcePlanGroupBy.Role
  ]),
  resourceRequest: PropTypes.object.isRequired,
  maxSkillLevel: PropTypes.number
};

export default ResourceRequestReadonlyItem;
